import { Component, ChangeDetectionStrategy, inject, computed, signal, Input } from '@angular/core'
import { SalesFunnelService } from '@core/services/sales-funnel/sales-funnel.service'
import { SalesFunnelHelper } from '../../../../core/helpers/sales-funnel.helper'
import { ISalesFunnelStage, IStageLeakage } from '../../../../core/models/dashboard.model'
import { ESalesFunnelView, EStageId } from '../../../../core/enum/sales-funnel.enum'
import { valueConstants } from '../../../../core/constants/valueConstants'
import { LEAD_EXCLUDED_STAGES } from '../../../../features/crm/constants/ruc.constants'
import { EiTooltipDirective } from '../../../../shared/directives'

@Component({
    selector: 'app-sales-funnel-overview',
    templateUrl: './sales-funnel-overview.component.html',
    styleUrls: ['./sales-funnel-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EiTooltipDirective],
})
export class SalesFunnelOverviewComponent {
    private readonly salesFunnelService = inject(SalesFunnelService)

    @Input() title: string = valueConstants.SALES_FUNNEL_DEFAULT_TITLE
    @Input() description: string = valueConstants.SALES_FUNNEL_DEFAULT_DESCRIPTION
    @Input() hideDroppedStages = true
    @Input() isRucContext = false
    @Input() showRevenue = true
    @Input() isTeamView = false

    currentView = signal<ESalesFunnelView>(ESalesFunnelView.FUNNEL)
    readonly isLoading = this.salesFunnelService.isLoading
    readonly error = this.salesFunnelService.error
    readonly stageMetrics = this.salesFunnelService.stageMetrics
    readonly keyMetrics = this.salesFunnelService.keyMetrics

    readonly stages = computed(() => {
        const metrics = this.stageMetrics()
        if (!metrics.length) {
            return []
        }
        const visibleMetrics = this.hideDroppedStages
            ? metrics.filter(
                  (metric) =>
                      metric.stage_id !== EStageId.DROPPED && metric.stage_id !== EStageId.DEFERRED,
              )
            : metrics
        const maxCount = Math.max(...metrics.map((item) => item.leads_count))
        const showRevenueInDataView =
            this.isRucContext && this.currentView() === ESalesFunnelView.DATA
        return SalesFunnelHelper.transformStagesToFunnelFormat(
            visibleMetrics,
            maxCount,
            this.isRucContext,
            showRevenueInDataView,
        )
    })

    readonly stageLeakages = computed(() => {
        const keyMetrics = this.keyMetrics()
        const metrics = this.stageMetrics()
        if (!keyMetrics || !metrics.length) {
            return []
        }
        return SalesFunnelHelper.transformLeakages(keyMetrics, metrics)
    })

    readonly hasError = computed(() => {
        return !!this.error()
    })

    readonly errorMessage = computed(() => {
        const error = this.error()
        if (!error) {
            return ''
        }
        if (error.includes('sales funnel')) {
            return 'Failed to load sales funnel data'
        }
        return 'Failed to load sales funnel data'
    })

    readonly ESalesFunnelView = ESalesFunnelView
    readonly valueConstants = valueConstants
    readonly salesFunnelTooltip = valueConstants.SALES_FUNNEL_DEFAULT_DESCRIPTION

    readonly dataTableHeaderSkeletonItems = [1, 2, 3, 4, 5, 6]
    readonly dataTableRowSkeletonItems = [1, 2, 3, 4, 5, 6, 7]
    readonly leakageCardSkeletonItems = [1, 2]

    switchView(view: ESalesFunnelView): void {
        this.currentView.set(view)
    }

    isActiveView(view: ESalesFunnelView): boolean {
        return this.currentView() === view
    }

    /**
     * Determines if revenue should be displayed for a given stage
     * @param stage The sales funnel stage
     * @returns true if revenue should be shown, false otherwise
     */

    shouldShowRevenue(stage: ISalesFunnelStage): boolean {
        const excludedStages = LEAD_EXCLUDED_STAGES
        return Boolean(
            this.showRevenue &&
                stage.revenue &&
                stage.revenue !== valueConstants.NA &&
                !excludedStages.has(stage.code),
        )
    }

    shouldShowConversion(stage: ISalesFunnelStage): boolean {
        return Boolean(
            stage.stageId !== EStageId.ALL_SCHOOLS &&
                stage.conversion &&
                stage.conversion !== valueConstants.NA,
        )
    }
}
